import React from 'react';
import { Button } from '../../../components/ui/Button';
import { AppIcon, IconName } from '../../../components/icons/AppIcon';
import SubscriptionCountdown from './SubscriptionCountdown';
import type { SubscriptionAlertInfo } from '../hooks/useSubscriptionAlert';

interface SubscriptionAlertModalProps {
    alert: SubscriptionAlertInfo;
    progressPercentage: number;
    onAction: () => void;
    onDismiss?: () => void;
}

const alertStyles: Record<string, { iconBg: string; iconColor: string; icon: IconName }> = {
    trial_ending: { iconBg: 'bg-blue-50', iconColor: 'text-blue-600', icon: 'zap' },
    payment_pending: { iconBg: 'bg-amber-50', iconColor: 'text-amber-600', icon: 'credit-card' },
    trial_expired: { iconBg: 'bg-red-50', iconColor: 'text-red-600', icon: 'alert-triangle' },
    blocked: { iconBg: 'bg-red-50', iconColor: 'text-red-600', icon: 'alert-triangle' },
    no_plan: { iconBg: 'bg-blue-50', iconColor: 'text-blue-600', icon: 'zap' },
};

const SubscriptionAlertModal: React.FC<SubscriptionAlertModalProps> = ({
    alert,
    progressPercentage,
    onAction,
    onDismiss,
}) => {
    const styles = alertStyles[alert.type || 'blocked'];
    const canDismiss = alert.severity !== 'blocked' && onDismiss;

    return (
        <div className="fixed inset-0 z-modal flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm" onClick={canDismiss ? onDismiss : undefined} />

            {/* Modal */}
            <div className="relative w-full max-w-md animate-scale-in bg-background rounded-2xl shadow-xl overflow-hidden border border-border">
                {/* Header (Clean white style) */}
                <div className="p-6 sm:p-8 pb-0">
                    {canDismiss && (
                        <button onClick={onDismiss} className="absolute top-4 right-4 p-1.5 text-muted hover:text-secondary hover:bg-surface-subtle rounded-full transition-colors z-10" title="Fechar">
                            <AppIcon name="close" className="w-5 h-5" />
                        </button>
                    )}

                    <div className="flex flex-col items-center text-center">
                        <div className={`w-16 h-16 rounded-full ${styles.iconBg} border border-subtle flex items-center justify-center mb-5 shrink-0 shadow-sm`}>
                            <AppIcon name={styles.icon} className={`w-8 h-8 ${styles.iconColor}`} />
                        </div>

                        <h2 className="text-xl font-semibold text-default mb-2 font-inter tracking-tight">
                            {alert.title}
                        </h2>
                        <p className="text-muted text-sm font-inter">
                            {alert.description}
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 sm:p-8 pt-6">
                    {/* Countdown */}
                    {alert.daysRemaining >= 0 && alert.type !== 'no_plan' && (
                        <div className="mb-6">
                            <SubscriptionCountdown
                                daysRemaining={alert.daysRemaining}
                                severity={alert.severity}
                                label={alert.type === 'trial_ending' ? 'dias de teste' : 'dias até bloqueio'}
                                showProgressBar
                                progressPercentage={progressPercentage}
                                size="lg"
                                className="w-full justify-center"
                            />
                        </div>
                    )}

                    {/* CTA */}
                    {alert.ctaLabel ? (
                        <Button size="lg" className="w-full justify-center gap-2" onClick={onAction}>
                            <AppIcon name={alert.type === 'payment_pending' ? 'credit-card' : 'zap'} className="w-5 h-5" />
                            {alert.ctaLabel}
                        </Button>
                    ) : (
                        onDismiss && (
                            <Button size="lg" variant="outline" className="w-full justify-center" onClick={onDismiss}>
                                Entendi
                            </Button>
                        )
                    )}

                    {/* Dismiss */}
                    {canDismiss && (
                        <button onClick={onDismiss} className="w-full mt-3 text-sm text-muted hover:text-default transition-colors">
                            Lembrar mais tarde
                        </button>
                    )}

                    {/* Support */}
                    <p className="mt-4 text-center text-xs text-muted">
                        Dúvidas? <a href="mailto:suporte@adlitics.com" className="text-blue-600 hover:underline">Fale conosco</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionAlertModal;
